import { useState } from 'react';
import { 
  DollarSign, Calendar, Clock, Download, Filter, Search,
  CheckCircle, AlertCircle, FileText, TrendingUp
} from 'lucide-react';

interface PayrollEntry {
  id: string;
  staffId: string;
  staffName: string;
  role: string;
  period: string;
  hoursWorked: number;
  hourlyRate: number;
  overtime: number;
  overtimeRate: number;
  bonus: number;
  deductions: number;
  totalPay: number;
  status: 'pending' | 'paid' | 'processing';
  paidDate?: string;
}

export default function StaffPayroll() {
  const [payroll, setPayroll] = useState<PayrollEntry[]>([
    {
      id: 'PAY-001',
      staffId: 'STF-001',
      staffName: 'María López',
      role: 'Limpieza',
      period: '2026-03',
      hoursWorked: 80,
      hourlyRate: 15,
      overtime: 10,
      overtimeRate: 22.5,
      bonus: 50,
      deductions: 20,
      totalPay: 1495,
      status: 'paid',
      paidDate: '2026-03-31',
    },
    {
      id: 'PAY-002',
      staffId: 'STF-002',
      staffName: 'Carlos Martínez',
      role: 'Mantenimiento',
      period: '2026-03',
      hoursWorked: 160,
      hourlyRate: 20,
      overtime: 5,
      overtimeRate: 30,
      bonus: 0,
      deductions: 50,
      totalPay: 3300,
      status: 'pending',
    },
  ]);

  const [selectedPeriod, setSelectedPeriod] = useState('2026-03');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'paid'>('all');

  const filteredPayroll = payroll.filter(entry => {
    const matchesPeriod = entry.period === selectedPeriod;
    const matchesStatus = filterStatus === 'all' || entry.status === filterStatus;
    return matchesPeriod && matchesStatus;
  });

  const stats = {
    totalPayroll: filteredPayroll.reduce((acc, entry) => acc + entry.totalPay, 0),
    totalHours: filteredPayroll.reduce((acc, entry) => acc + entry.hoursWorked, 0),
    pendingPayments: filteredPayroll.filter(e => e.status === 'pending').length,
    avgHourlyRate: (filteredPayroll.reduce((acc, e) => acc + e.hourlyRate, 0) / filteredPayroll.length).toFixed(2),
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      processing: 'bg-blue-500/20 text-blue-400',
      paid: 'bg-green-500/20 text-green-400',
    };
    const labels = {
      pending: 'Pendiente',
      processing: 'Procesando',
      paid: 'Pagado',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Nómina</h2>
          <p className="text-[var(--v-text-secondary)]">Gestión de pagos y compensaciones</p>
        </div>
        <div className="flex gap-2">
          <button className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2">
            <Download className="w-5 h-5" />
            Exportar
          </button>
          <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Procesar Pagos
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-[var(--v-text-secondary)] text-sm">Total Nómina</p>
          <p className="text-3xl font-bold text-[var(--v-text-primary)] mt-1">${stats.totalPayroll.toLocaleString()}</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-[var(--v-text-secondary)] text-sm">Horas Trabajadas</p>
          <p className="text-3xl font-bold text-blue-400 mt-1">{stats.totalHours}h</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-[var(--v-text-secondary)] text-sm">Pagos Pendientes</p>
          <p className="text-3xl font-bold text-yellow-400 mt-1">{stats.pendingPayments}</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-[var(--v-text-secondary)] text-sm">Tarifa Promedio</p>
          <p className="text-3xl font-bold text-purple-400 mt-1">${stats.avgHourlyRate}/h</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="bg-[var(--v-surface-1)] text-white px-4 py-2 rounded-lg border border-[var(--v-surface-3)]"
        >
          <option value="2026-03">Marzo 2026</option>
          <option value="2026-02">Febrero 2026</option>
          <option value="2026-01">Enero 2026</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          className="bg-[var(--v-surface-1)] text-white px-4 py-2 rounded-lg border border-[var(--v-surface-3)]"
        >
          <option value="all">Todos los estados</option>
          <option value="pending">Pendiente</option>
          <option value="paid">Pagado</option>
        </select>
      </div>

      {/* Payroll Table */}
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--v-surface-3)]">
                <th className="text-left p-4 text-[var(--v-text-secondary)] font-medium">Empleado</th>
                <th className="text-left p-4 text-[var(--v-text-secondary)] font-medium">Horas</th>
                <th className="text-left p-4 text-[var(--v-text-secondary)] font-medium">Tarifa</th>
                <th className="text-left p-4 text-[var(--v-text-secondary)] font-medium">Extras</th>
                <th className="text-left p-4 text-[var(--v-text-secondary)] font-medium">Total</th>
                <th className="text-left p-4 text-[var(--v-text-secondary)] font-medium">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {filteredPayroll.map((entry) => (
                <tr key={entry.id} className="hover:bg-slate-700/30">
                  <td className="p-4">
                    <p className="text-white font-medium">{entry.staffName}</p>
                    <p className="text-[var(--v-text-secondary)] text-sm">{entry.role}</p>
                  </td>
                  <td className="p-4">
                    <p className="text-white">{entry.hoursWorked}h</p>
                    {entry.overtime > 0 && (
                      <p className="text-yellow-400 text-sm">+{entry.overtime}h extra</p>
                    )}
                  </td>
                  <td className="p-4">
                    <p className="text-white">${entry.hourlyRate}/h</p>
                    {entry.overtime > 0 && (
                      <p className="text-yellow-400 text-sm">${entry.overtimeRate}/h extra</p>
                    )}
                  </td>
                  <td className="p-4">
                    {entry.bonus > 0 && (
                      <p className="text-green-400 text-sm">+${entry.bonus} bono</p>
                    )}
                    {entry.deductions > 0 && (
                      <p className="text-red-400 text-sm">-${entry.deductions} deducción</p>
                    )}
                  </td>
                  <td className="p-4">
                    <p className="text-xl font-bold text-[var(--v-text-primary)]">${entry.totalPay.toLocaleString()}</p>
                  </td>
                  <td className="p-4">
                    {getStatusBadge(entry.status)}
                    {entry.paidDate && (
                      <p className="text-slate-500 text-xs mt-1">{entry.paidDate}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
